import {Component, EventEmitter, Input, Output, TemplateRef, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgOptionTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';
import {NgTemplateOutlet} from '@angular/common';

export interface SelectOptionContext<T> {
  $implicit: T;
  index: number;
}

@Component({
  selector: 'cqme-select',
  standalone: true,
  imports: [
    NgSelectComponent,
    FormsModule,
    NgTemplateOutlet,
    NgOptionTemplateDirective
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss'
})
export class SelectComponent<TItem extends object, TValue = TItem> {
  @Input() current: TValue | TValue[] | undefined;
  @Output() currentChange = new EventEmitter<TValue | TValue[] | undefined>();

  @Input() options: TItem[] = [];

  @Input() labelKey!: keyof TItem;
  @Input() valueKey?: keyof TItem;

  @Input() disabled = false;
  @Input() clearable = false;
  /** Allow selecting several options at once; `current`/`currentChange` then carry an array of values. */
  @Input() multiple = false;
  /** Control height: `sm` (compact, default) or `md` to line up with 40px inputs (e.g. date/text fields). */
  @Input() size: 'sm' | 'md' = 'sm';
  @Input() placeholder = '';
  /** Accessible name for the search input — set it when the visible label isn't programmatically associated. */
  @Input() ariaLabel?: string;

  @Input() optionTemplate?: TemplateRef<SelectOptionContext<TItem>>;

  /** The inner ng-select, so {@link reset} can clear the *view*, not just the bound value. */
  @ViewChild(NgSelectComponent) private ngSelect?: NgSelectComponent;

  /**
   * Return the control to its placeholder — for the "action, not a value" case: a picker whose choice is
   * consumed immediately (it links something, adds a row, runs a command) and which must then start over.
   *
   * Setting {@link current} back to `undefined` from the parent's handler is **not** enough, and the reason is
   * worth writing down because the broken version looks correct. `current` is assigned inside this component's
   * own change handler, so Angular's dirty check never records the picked value; a parent that clears the
   * bound expression in the same turn leaves it looking unchanged from the last check, `[ngModel]="current"`
   * is therefore never written again, and ng-select goes on displaying the choice the parent already acted on.
   *
   * The selection has to be cleared on ng-select itself. `writeValue` is the ControlValueAccessor entry point,
   * so it clears the view without emitting a change back into the parent — and, unlike ng-select's own
   * `clearModel()`, without the `clearable` gate that would silently no-op on an action picker (which has no
   * reason to show a clear button).
   */
  reset(): void {
    this.current = undefined;
    this.ngSelect?.writeValue(undefined);
  }

  get bindLabel(): string {
    return String(this.labelKey);
  }

  get hasBindValue(): boolean {
    return this.valueKey !== undefined;
  }

  get bindValue(): string {
    return String(this.valueKey);
  }

  onCurrentChange(value: TValue | TValue[] | undefined): void {
    this.currentChange.emit(value);
  }

  defaultSearchFn = (term: string, item: TItem): boolean => {
    return String(item[this.labelKey]).toLowerCase().includes(term.toLowerCase());
  };

  @Input() searchFn: (term: string, item: TItem) => boolean = this.defaultSearchFn;
}
